import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  Divider,
  IconButton,
  Drawer,
  InputAdornment,
  Checkbox,
  Tooltip,
  Tabs,
  Tab,
  Avatar,
} from "@mui/material";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { DELETE_DEAL, VERIFY_DEALS } from "src/graphql/mutations";
import { GET_DEALS, GET_DEAL } from "src/graphql/query";
import CustomTable from "src/components/CustomTable";
import Loader from "src/components/Loader";
import ErrorComponent from "src/components/ErrorComponent";
import DeleteIcon from "@mui/icons-material/Delete";
import Select from "react-select";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  DealCategory,
  DealTypes,
  FilterDeals,
  dealCategoryOptions,
  dealFilterOptions,
} from "./dealCategory";
import CustomButton from "src/components/CustomButton";
import CreateDeal from "./CreateDeal";
import MenuIcon from "@mui/icons-material/Menu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteModel from "src/components/Common/DeleteModel";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DealDetailPage from "./DealDetailModal";
import styles from "./Deal.module.css";
import { ClearIcon } from "@mui/x-date-pickers";
import { debounce } from "src/utils/debounce";
import {
  DELETE_All_DEALS,
  DELETE_MULTIPLE_DEALS,
} from "src/graphql/DeleteMutation";
import { UPDATE_DEAL } from "src/graphql/mutations";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { toast } from "react-toastify";
import { getRole, hasPermissionPage } from "src/components/Common/Utils";
import ActionMenu from "../Business/ActionMenu";
import ApprovalIcon from "@mui/icons-material/Approval";
import VerifyDealsDialog from "./VerifyDeals";

const Deal: React.FC = () => {
  const [list, setList] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategories, setSelectedCategories] = useState<DealCategory[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterDeals[]>([]);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 50 });
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [total, setTotal] = useState(0);
  const [DeleteDeals] = useMutation(DELETE_DEAL);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [dealData, setDealData] = useState<any>(null);
  const [deleteDealId, setDeleteDealId] = useState<string | null>(null);
  const [openDeleteModel, setOpenDeleteModel] = useState<boolean>(false);
  const [openDeleteMultipleModel, setOpenDeleteMultipleModel] = useState<boolean>(false);
  const [openDeleteAllModel, setOpenDeleteAllModel] = useState<boolean>(false);
  const [DeleteMultipleDeals] = useMutation(DELETE_MULTIPLE_DEALS);
  const [DeleteAllDeals] = useMutation(DELETE_All_DEALS);
  const [selectedRows, setSelectedRows] = useState([]);
  const isSuperAdmin = getRole() === "SuperAdmin";
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [allDeals, setAllDeals] = useState<any[]>([]);
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [currentDealForStatusChange, setCurrentDealForStatusChange] = useState<any>(null);
  const [verifyingDeals, setVerifyingDeals] = useState<string | null>(null);
  const [verifyDealsValue, setVerifyDealsValue] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [updateDeals] = useMutation(UPDATE_DEAL);
  const [VerifyDeals] = useMutation(VERIFY_DEALS);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleDuplicate = async (dealId: string) => {
    try {
      const response = await GetDeal({ variables: { _id: dealId } });
      const dealData = response.data?.getDeal?.data;

      if (dealData) {
        setIsDuplicating(true);
        const duplicatedDeal = {
          ...dealData,
          _id: undefined,
          title: `${dealData?.title} (Duplicate)`,
        };
        setDealData(duplicatedDeal);
        setOpenModal(true);
      }
    } catch (error) {
      console.error("Error fetching deal data for duplication:", error);
      toast.error("Failed to fetch deal data for duplication");
    }
  };

  const handleVerify = async (dealId: string, activityStatus: string) => {
    try {
      const res = await VerifyDeals({
        variables: {
          input: {
            _id: dealId,
            activityStatus,
            reason: reason,
          },
        },
      });
      if (res?.errors) {
        throw new Error(
          `GraphQL error! message: ${res.errors
            .map((error: any) => error.message)
            .join(", ")}`
        );
      }

      refetch();
      setReason("");
      setVerifyDealsValue("");
      toast.success(res?.data?.verifyDeals?.message);
      setVerifyingDeals(null);
    } catch (error: any) {
      console.error("Error verifying Deals:", error);
      toast.error(error?.message);
    }
  };

  const COLUMNS = [
    ...(isSuperAdmin
      ? [
        {
          headerName: (
            <Box display={"flex"} alignItems={"center"}>
              <Checkbox
                checked={
                  selectedRows.length === list.length && list.length > 0
                }
                indeterminate={
                  selectedRows.length > 0 && selectedRows.length < list.length
                }
                onChange={(event) => handleSelectAll(event.target.checked)}
              />
            </Box>
          ),
          field: "select",
          flex: 0.5,
          sortable: false,
          filterable: false,
          disableClickEventBubbling: true,
          renderCell: ({ row }: any) => (
            <Checkbox
              checked={selectedRows.includes(row?._id as never)}
              onChange={() => handleRowSelect(row._id)}
            />
          ),
        },
      ]
      : []),
    {
      headerName: "Updated Date",
      field: "updatedAt",
      width: 120,
      renderCell: ({ row }: any) => {
        const date = new Date(row?.updatedAt);
        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleString("en-US", { month: "short" }); // Apr
        const year = date.getFullYear();
        return <>{`${day}, ${month} ${year}`}</>;
      },
    },
    {
      headerName: "Title / Category",
      field: "title",
      flex: 2,
      renderCell: ({ row }: any) => (
        <>
          <Box sx={{ cursor: "pointer" }} onClick={() => handleView(row)}>
            <Typography variant="body2" fontWeight={500}>
              {row?.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {row?.category || 'N/A'}
            </Typography>
          </Box>
        </>
      ),
    },
    // { headerName: "Category", field: "category", flex: 2 },
    {
      headerName: "Business / Area",
      field: "businessProfile.businessName",
      flex: 2,
      renderCell: ({ row }: any) => {
        const getInitials = (name: string) => {
          const words = name.trim().split(" ");
          if (words.length === 1) return words[0][0].toUpperCase();
          return (words[0][0] + words[1][0]).toUpperCase();
        };

        return (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
          >
            {row?.businessProfile?.brandLogo ? (
              <img
                src={row?.businessProfile?.brandLogo}
                alt="Brand Logo"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Avatar sx={{ width: 40, height: 40 }}>
                {getInitials(row?.businessProfile?.businessName || "N/A")}
              </Avatar>
            )}
            <Box display="flex" flexDirection="column">
              <Typography variant="body2" fontWeight={500}>
                {row?.businessProfile?.businessName || "N/A"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {row?.area || "N/A"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      headerName: "Offer Type",
      field: "currentPrice",
      flex: 1,
      renderCell: ({ row }: any) => (
        <Box display="flex" flexDirection="column">
          <Typography variant="body2" fontWeight={500}>
            {row?.discountType?.charAt(0)?.toUpperCase() + row?.discountType?.slice(1)?.toLowerCase()}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {/* {row?.discountType === "PERCENTAGE"
              ? `₹${row?.currentPrice} (${row?.discountPercentage}%)`
              : "-"
            } */}
            {row?.currentPrice === 0 || row?.currentPrice === null
              ? row?.discountPercentage != null
                ? `${row.discountPercentage}%`
                : "-"
              : row?.discountType === "PERCENTAGE" && row?.discountPercentage != null
                ? `₹${row.currentPrice} (${row.discountPercentage}%)`
                : `₹${row.currentPrice ?? "-"}`
            }

          </Typography>
        </Box>
      ),
    },
    {
      headerName: "Offer",
      field: "discountPrice",
      flex: 1,
      renderCell: ({ row }: any) => (
        <Box>
          {row?.discountPrice === 0 ? "-" : `₹ ${row?.discountPrice}`}
        </Box>
      ),
    },
    {
      headerName: "Status",
      field: "activityStatus",
      flex: 1.2,
      renderCell: ({ row }: any) => {
        return (
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Box
              sx={{
                color: row.activityStatus === "ACTIVE" ? "green" : row.activityStatus === "PENDING" ? "orange" : row.activityStatus === "ARCHIVE" ? "grey" : "red",
                fontWeight: "bold"
              }}
            >
              {row.activityStatus.charAt(0).toUpperCase() + row.activityStatus.slice(1).toLowerCase()}
            </Box>
            {hasPermissionPage("Deals", "verify") && (
              <Tooltip title="Verify Deals">
                <IconButton
                  onClick={() => setVerifyingDeals(row._id)}
                  aria-label="status"
                  style={{ color: "#869986" }}
                >
                  <ApprovalIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      headerName: "Actions",
      flex: 1.2,
      renderCell: ({ row }: { row: { _id: string } }) => (
        <Box>
          {hasPermissionPage("Deals", "update") && (
            <Tooltip title="Edit">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row?._id);
                }}
                aria-label="edit"
                className={styles.editButton}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissionPage("Deals", "delete") && (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => handleOpenDeleteModel(row._id)}
                aria-label="delete"
                className={styles.editButton}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          {hasPermissionPage("Deals", "add") && (
            <Tooltip title="Duplicate">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicate(row?._id);
                }}
                aria-label="duplicate"
                className={styles.editButton}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const { loading, error, data, refetch } = useQuery(GET_DEALS, {
    variables: {
      page: 1,
      limit: 10000,
      selectedCategory: selectedCategories.length
        ? selectedCategories
        : undefined,
      filter: filterOptions.length ? filterOptions : undefined,
      // activityStatus: activeTab === 0 ? ["Active", "upcoming"] : ["Archived"]
    },
  });

  const filteredDeals = useMemo(() => {
    if (!allDeals.length) return [];

    switch (activeTab) {
      case 0: // Active tab
        return allDeals.filter(deal => deal.activityStatus === "ACTIVE");
      case 1: // Pending tab
        return allDeals.filter(deal => deal.activityStatus === "PENDING");
      case 2: // Rejected tab
        return allDeals.filter(deal => deal.activityStatus === "REJECTED");
      case 3: // Archived tab
        return allDeals.filter(deal => deal.activityStatus === "ARCHIVE");
      default:
        return allDeals;
    }
  }, [allDeals, activeTab]);




  const [GetDeal] = useLazyQuery(GET_DEAL);

  const handleRowSelect = (id: any) => {
    setSelectedRows((prevSelectedRows: any) =>
      prevSelectedRows.includes(id)
        ? prevSelectedRows.filter((rowId: any) => rowId !== id)
        : [...prevSelectedRows, id]
    );
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allRowIds = list.map((row: any) => row._id);
      setSelectedRows(allRowIds as any);
    } else {
      setSelectedRows([]);
    }
  };

  const handleMultipleDelete = async () => {
    try {
      const res = await DeleteMultipleDeals({
        variables: {
          _id: selectedRows,
        },
      });
      if (res?.data?.deleteMultipleDeals?.statusCode === 200) {
        toast.success(res?.data?.deleteMultipleDeals?.message);
        refetch();
      } else {
        throw new Error(res?.data?.deleteMultipleDeals?.message);
      }
    } catch (errors: any) {
      console.error(error);
      toast.error(errors?.message);
    } finally {
      setOpenDeleteMultipleModel(false);
    }
  };

  const handleAllDelete = async () => {
    try {
      const res = await DeleteAllDeals();
      if (res?.data?.deleteAllDeals?.statusCode === 200) {
        toast.success(res?.data?.deleteAllDeals?.message);
        refetch();
      } else {
        throw new Error(res?.data?.deleteAllDeals?.message);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message);
    } finally {
      setOpenDeleteAllModel(false);
    }
  };


  const paginatedDeals = useMemo(() => {
    const startIndex = pagination.page * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredDeals.slice(startIndex, endIndex);
  }, [filteredDeals, pagination.page, pagination.pageSize]);

  useEffect(() => {
    setList(paginatedDeals);
    setTotal(filteredDeals.length);
  }, [paginatedDeals, filteredDeals]);

  useEffect(() => {
    if (data?.getDealsForAdmin?.data) {
      setAllDeals(data?.getDealsForAdmin?.data);
    }
  }, [data]);


  const handleView = async (deal: { _id: string }) => {
    try {
      const response = await GetDeal({ variables: { _id: deal._id } });
      setDealData(response.data?.getDeal?.data);
      setOpenViewModal(true);
    } catch (err) {
      console.error("Error fetching deal data:", err);
    }
  };

  const handleEdit = async (dealId: string) => {
    try {
      const response = await GetDeal({ variables: { _id: dealId } });
      setDealData(response.data?.getDeal?.data);
      setOpenModal(true);
    } catch (err) {
      console.error("Error fetching deal data:", err);
    }
  };

  const handleDelete = async () => {
    if (!deleteDealId) return;
    try {
      await DeleteDeals({ variables: { _id: deleteDealId } });
      toast.success("Deal Deleted Successfully");
      refetch();
    } catch (error: any) {
      console.error("Failed to delete deal", error);
    } finally {
      setOpenDeleteModel(false);
      setDeleteDealId(null);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedRefetch(value);
  };

  const handleCategoryChange = (selectedOptions: any) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((option: any) => option.value.trim())
      : [];
    setSelectedCategories(selectedValues);
    refetch();
  };

  const handleFilterChange = (selectedOptions: any) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((option: any) => option.value.trim())
      : [];
    setFilterOptions(selectedValues);
    refetch();
  };

  const debouncedRefetch = useCallback(
    debounce((term: string) => {
      refetch({
        search: term,
        page: 1,
        limit: 10000,
        selectedCategory: selectedCategories.length
          ? selectedCategories.map((category) => category)
          : undefined,
        filter: filterOptions.length
          ? filterOptions.map((filter) => filter)
          : undefined,
      }).then(({ data }) => {
        if (data?.getDealsForAdmin?.data) {
          setAllDeals(data.getDealsForAdmin.data);
          setPagination(prev => ({ ...prev, page: 0 }));
        }
      });
    }, 1000),
    [refetch, selectedCategories, filterOptions, activeTab]
  );

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
  };

  const handleOpenDeleteModel = (dealId: string) => {
    setDeleteDealId(dealId);
    setOpenDeleteModel(true);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch({ search: "", page: 1, limit: pagination.pageSize });
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  if (openViewModal) {
    return (
      <DealDetailPage
        dealData={dealData}
        onClose={() => setOpenViewModal(false)}
      />
    );
  }

  return openModal ? (
    <CreateDeal
      reload={() => refetch()}
      dealData={dealData}
      isDuplicating={isDuplicating}
      onClose={() => {
        setDealData(null);
        setOpenModal(false);
        setIsDuplicating(false);
      }}
    />
  ) : (
    <>
      <Box p={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4" style={{ color: "#00C5B9" }}>
            Deals
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Box width={200}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {isSuperAdmin && (
              <ActionMenu
                isSuperAdmin={isSuperAdmin}
                setOpenDeleteMultipleModel={setOpenDeleteMultipleModel}
                setOpenDeleteAllModel={setOpenDeleteAllModel}
              />
            )}
            {hasPermissionPage("Deals", "add") && (
              <Box>
                <CustomButton
                  onClick={() => setOpenModal(true)}
                  variant="contained"
                >
                  Create
                </CustomButton>
              </Box>
            )}
            <Tooltip title="Filter">
              <IconButton onClick={() => setOpenDrawer(true)} aria-label="menu">
                <MenuIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Add Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Active Deals" />
            <Tab label="Pending Deals" />
            <Tab label="Rejected Deals" />
            <Tab label="Expired Deals" />
          </Tabs>
        </Box>

        <CustomTable
          columns={COLUMNS as any}
          data={list}
          totalCount={total}
          paginationModel={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />

        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
        >
          <Box p={2} width="250px">
            <Typography variant="h6">Filters</Typography>
            <Divider />
            <Box mt={2}></Box>
          </Box>
        </Drawer>

        <DeleteModel
          open={openDeleteModel}
          onClose={() => setOpenDeleteModel(false)}
          onConfirm={handleDelete}
        />
      </Box>
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        sx={{ width: 250, flexShrink: 0 }}
      >
        <Box p={2} width={250} mt={10}>
          <Typography variant="h4" gutterBottom style={{ color: "#00C5B9" }}>
            Filters
          </Typography>
          <Box>
            <Typography variant="h6" gutterBottom>
              Category
            </Typography>
            <Select
              isMulti
              options={dealCategoryOptions}
              value={dealCategoryOptions.filter((option) =>
                selectedCategories.includes(option.value)
              )}
              onChange={handleCategoryChange}
              placeholder="Select Categories"
            />
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              Filter
            </Typography>
            <Select
              isMulti
              options={dealFilterOptions}
              value={dealFilterOptions.filter((option) =>
                filterOptions.includes(option.value)
              )}
              onChange={handleFilterChange}
              placeholder="Select Filters"
            />
          </Box>
        </Box>
      </Drawer>
      <VerifyDealsDialog
        label="Deals"
        handleVerify={handleVerify}
        setVerifyingData={setVerifyingDeals}
        verifyingData={verifyingDeals}
        verifyDataValue={verifyDealsValue}
        reason={reason}
        setVerifyDataValue={setVerifyDealsValue}
        setReason={setReason}
      />

      {openDeleteMultipleModel && (
        <DeleteModel
          open={openDeleteMultipleModel}
          onClose={() => {
            setOpenDeleteMultipleModel(false);
          }}
          onConfirm={handleMultipleDelete}
          message={`Are you sure you want to delete ${selectedRows?.length ?? 0
            } deals?`}
        />
      )}
      {openDeleteAllModel && (
        <DeleteModel
          open={openDeleteAllModel}
          onClose={() => {
            setOpenDeleteAllModel(false);
          }}
          onConfirm={handleAllDelete}
          message="Are you sure you want to delete all deals?"
        />
      )}
    </>
  );
};

export default Deal;